import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { getErrorPresentation } from '@/lib/errors';

interface ServerErrorProps {
  error?: Error;
  resetErrorBoundary?: () => void;
}

export default function ServerError({ error, resetErrorBoundary }: ServerErrorProps) {
  const { t } = useTranslation();

  const { isServerError, requestId } = getErrorPresentation(error);

  const handleReload = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="text-center space-y-6 max-w-md">
        <div className="space-y-2">
          {isServerError ? (
            <h1 className="text-8xl font-bold tabular-nums vess-iridescent-text">500</h1>
          ) : (
            <AlertTriangle className="h-16 w-16 mx-auto text-muted-foreground" />
          )}
          <p className="text-2xl font-semibold tracking-tight">
            {isServerError ? t('errors.serverError') : t('errors.applicationError')}
          </p>
          {requestId && (
            <p className="text-xs text-muted-foreground font-mono">
              {t('errors.supportReference', { requestId: requestId.slice(0, 8) })}
            </p>
          )}
        </div>

        <p className="text-muted-foreground text-sm">
          {isServerError
            ? t('errors.serverErrorMessage')
            : t('errors.applicationErrorMessage')}
        </p>

        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => window.location.href = '/'}>
            {t('errors.goHome')}
          </Button>
          <Button className="vess-btn-primary gap-2" onClick={handleReload}>
            <RefreshCw className="h-4 w-4" />
            {t('errors.tryAgain')}
          </Button>
        </div>
      </div>
    </div>
  );
}
